import React from "react";
import useCinematicStore from '@store/useCinematicStore';
import useGameStore from '@store/useGameStore';
import { GameStatus } from '@utils/enums';
import { EMPTY_ARRAY } from '@utils/constants';
import "./PlannedActionLine.css";

export default function PlannedActionLine({
  fromX,
  fromY,
  toX,
  toY,
  order, // Keep for potential future use but not displayed
  color = "#FFFFFF",
  isHighlighted = false,
  actionSlug,
  actionId,
  action, // Full action data
}) {
  // ✅ VALIDACIÓN - Verificar que todas las coordenadas sean válidas
  if (typeof fromX !== 'number' || typeof fromY !== 'number' || 
      typeof toX !== 'number' || typeof toY !== 'number' ||
      isNaN(fromX) || isNaN(fromY) || isNaN(toX) || isNaN(toY) ||
      !isFinite(fromX) || !isFinite(fromY) || !isFinite(toX) || !isFinite(toY)) {
    console.error("[PlannedActionLine] Coordenadas inválidas:", { fromX, fromY, toX, toY });
    return null;
  }

  // ✅ NUEVA VALIDACIÓN - Detectar acciones self-target (mismo origen y destino)
  const deltaX = toX - fromX;
  const deltaY = toY - fromY;
  const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  
  // Si la distancia es muy pequeña (prácticamente el mismo punto), es una acción self-target
  const isSelfTarget = distance < 5; // Tolerancia de 5 píxeles
  
  if (isSelfTarget) {
    /*console.info("[PlannedActionLine] Acción self-target detectada, no renderizando línea:", {
      fromX, fromY, toX, toY, distance, actionSlug, actionId
    });*/
    return null; // No renderizar línea para acciones self-target
  }

  const pathD = `M ${fromX},${fromY} L ${toX},${toY}`;

  const strokeColor = isHighlighted ? "#FFD700" : color;

  // Validate we have the action data - FAIL LOUDLY if missing
  if (!action?.action) {
    throw new Error(`[PlannedActionLine] Missing action data for actionId ${actionId}. Action must be hydrated with action definition!`);
  }

  // Check if this is a movement action (is_movement is 0 or 1, not boolean)
  if (action.action.is_movement === undefined || action.action.is_movement === null) {
    throw new Error(`[PlannedActionLine] Missing is_movement property for action ${actionSlug} (id: ${actionId})`);
  }

  // Convert is_movement from number (0/1) to boolean
  const isMovement = action.action.is_movement === 1;

  // Debug log to see the action structure and movement flag
  /*console.log('[PlannedActionLine]['+ actionId+']['+ actionSlug+']:', {
    isMovement,
    is_movement_raw: action.action.is_movement,
    actionObject: action,
  });*/
  
  // ✅ Use reactive subscriptions instead of getState() to react to changes
  const game = useGameStore((state) => state.game);
  const { currentStep } = useCinematicStore();
  const currentStepIndex = currentStep; // Legacy compatibility
  
  if (!game) {
    console.warn("[PlannedActionLine] Game object is missing!");
  }
  
  const isCinematicPhase = game?.status === GameStatus.Resolving || game?.status === GameStatus.Resolved;
  
  // Encontrar si esta acción ya se ejecutó
  let isCompleted = false;
  if (isCinematicPhase && actionId && game?.resolution_queue) {
    const actionIndex = game.resolution_queue.findIndex(event => event.id === actionId);
    // Action is completed only if it's BEFORE the current step (not including current)
    // currentStep=0 (Initial State) -> no lines completed
    // currentStep=1 (First action) -> only action at index 0 completed
    isCompleted = actionIndex >= 0 && actionIndex < currentStepIndex;
  } else if (isCinematicPhase && !game?.resolution_queue) {
    console.warn("[PlannedActionLine] resolution_queue is missing during cinematic phase!");
  }
  
  // ✅ Crear ID único para el marker para evitar conflictos
  const markerId = `arrowhead-${Math.random().toString(36).substr(2, 9)}`;

  // ✅ CORRECCIÓN PRINCIPAL - Calcular dimensiones del SVG para evitar valores NaN
  // Usar un margen mínimo para evitar dimensiones muy pequeñas
  const margin = Math.max(20, distance * 0.1); // Margen dinámico basado en distancia
  const minX = Math.min(fromX, toX) - margin;
  const minY = Math.min(fromY, toY) - margin;
  const maxX = Math.max(fromX, toX) + margin;
  const maxY = Math.max(fromY, toY) + margin;
  
  const svgWidth = maxX - minX;
  const svgHeight = maxY - minY;

  // ✅ VALIDACIÓN MEJORADA - Asegurar dimensiones mínimas para evitar problemas
  const minDimension = 40; // Dimensión mínima para el SVG
  const finalWidth = Math.max(svgWidth, minDimension);
  const finalHeight = Math.max(svgHeight, minDimension);

  // ✅ VALIDACIÓN - Verificar que las dimensiones finales sean válidas
  if (isNaN(finalWidth) || isNaN(finalHeight) || finalWidth <= 0 || finalHeight <= 0 ||
      !isFinite(finalWidth) || !isFinite(finalHeight)) {
    console.error("[PlannedActionLine] Dimensiones SVG inválidas:", { 
      finalWidth, finalHeight, svgWidth, svgHeight, minX, minY, maxX, maxY, distance
    });
    return null;
  }

  // ✅ AJUSTAR POSICIÓN si las dimensiones fueron expandidas
  const adjustedLeft = minX - (finalWidth - svgWidth) / 2;
  const adjustedTop = minY - (finalHeight - svgHeight) / 2;

  return (
    <svg 
      className={`planned-action-line planned-action-line-action-${actionSlug} id${actionId} ${isCompleted ? 'completed' : ''}`}
      width={finalWidth}
      height={finalHeight}
      viewBox={`${adjustedLeft} ${adjustedTop} ${finalWidth} ${finalHeight}`}
      style={{
        position: 'absolute',
        left: adjustedLeft,
        top: adjustedTop,
        overflow: 'visible',
        pointerEvents: 'none'
      }}
    >
      <defs>
        {/* ✅ Flecha con borde blanco y relleno del color del equipo */}
        <marker
          id={markerId}
          markerWidth="5"
          markerHeight="5"
          refX="4"
          refY="2.5"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          {/* Borde de la flecha (blanco) */}
          <polygon 
            points="0 0, 4 2.5, 0 5" 
            fill="white"
            stroke="none"
          />
          {/* Relleno de la flecha (color del equipo) */}
          <polygon 
            points="0.8 1, 3.2 2.5, 0.8 4" 
            fill={strokeColor}
            stroke="none"
          />
        </marker>
      </defs>

      {/* ✅ Línea con borde blanco y relleno del color del equipo */}
      {/* Línea de fondo (borde blanco) */}
      <path
        d={pathD}
        stroke="white"
        strokeWidth={isMovement ? 5 : 4}
        strokeLinecap="round"
        strokeDasharray={isMovement ? "5 15" : "none"}
        fill="none"
        markerEnd={`url(#${markerId})`}
        className="action-line-border"
      />
      {/* Línea superior (color del equipo) */}
      <path
        d={pathD}
        stroke={strokeColor}
        strokeWidth={isMovement ? 4 : 3}
        strokeLinecap="round"
        strokeDasharray={isMovement ? "5 15" : "none"}
        fill="none"
        className="action-line-fill"
      />
      
      {/* ✅ Removed sequence number from path - now shown near action icon */}
    </svg>
  );
}

